import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { AuthAside } from "@/pages/Login";

const Shell = ({ title, text, children, testid }) => (
  <div className="grid min-h-screen lg:grid-cols-2" data-testid={testid}>
    <AuthAside />
    <main className="flex flex-col justify-center px-6 py-12 sm:px-12">
      <div className="mx-auto w-full max-w-sm fade-up">
        <div className="lg:hidden"><Logo size={40} /></div>
        <h2 className="mt-10 text-3xl font-extrabold lg:mt-0">{title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{text}</p>
        {children}
        <p className="mt-6 text-center text-sm"><Link to="/login" className="font-semibold underline-offset-4 hover:underline" data-testid="back-to-login-link">Retour à la connexion</Link></p>
      </div>
    </main>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell testid="forgot-password-page" title="Mot de passe oublié" text="Recevez un lien sécurisé, valable 1 heure.">
      {sent ? (
        <div className="card-surface tone-wash tone-mint mt-8 flex items-start gap-3 p-4 text-sm" data-testid="forgot-password-sent">
          <MailCheck className="h-5 w-5 shrink-0 text-sage" />
          Si un compte existe pour cette adresse, un email vient d'être envoyé. Pensez à vérifier vos courriers indésirables.
        </div>
      ) : (
        <form className="mt-8 space-y-4" onSubmit={submit}>
          <div><Label htmlFor="fp-email">Email</Label><Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11 rounded-xl" data-testid="forgot-password-email-input" /></div>
          {error && <p className="text-sm text-destructive" role="alert" data-testid="forgot-password-error">{error}</p>}
          <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="forgot-password-submit">Envoyer le lien</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password }); setDone(true); setTimeout(() => navigate("/login"), 2000); } catch (err) { setError(formatApiError(err)); }
  };
  return (
    <Shell testid="reset-password-page" title="Nouveau mot de passe" text="Toutes vos sessions ouvertes seront déconnectées.">
      {done ? <p className="mt-8 text-sm text-success" data-testid="reset-password-done">Mot de passe modifié. Redirection…</p> : (
        <form className="mt-8 space-y-4" onSubmit={submit}>
          <div><Label htmlFor="rp-1">Nouveau mot de passe (8 caractères min.)</Label><PasswordInput id="rp-1" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-11 rounded-xl" data-testid="reset-password-input" /></div>
          <div><Label htmlFor="rp-2">Confirmation</Label><PasswordInput id="rp-2" autoComplete="new-password" minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 h-11 rounded-xl" data-testid="reset-password-confirm-input" /></div>
          {error && <p className="text-sm text-destructive" role="alert" data-testid="reset-password-error">{error}</p>}
          <Button type="submit" className="h-11 w-full rounded-full" data-testid="reset-password-submit">Enregistrer</Button>
        </form>
      )}
    </Shell>
  );
}
